import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Box, Flex, Stack } from '@chakra-ui/layout';
import {
  Button,
  Label,
  RadioInput,
  TextInput,
  Unform,
  AddObservation,
  AuthorizeView,
  AutocompleteInput,
  Loading,
  SelectInput,
} from 'components';
import { BodyProps } from 'constants/MultipleAcorddions';
import { ThirdPartyAssessmentCtx } from 'pages';
import { checkPermission } from 'utils/authentication';

import TPA from 'models/Forms/TPA';
import CommentedAnswer, { yo } from 'models/CommentedAnswer';
import { getCommonProps } from 'utils/CommonPropperties';
import { getAllClients } from 'services/client';
import { getLoggedUser } from 'utils/storage';

type ClientProps = {
  name: string;
  id: React.ReactText;
};

const Item: React.FC<BodyProps> = ({ functionCallback, index, submit, completed }) => {
  const ctx = useContext(ThirdPartyAssessmentCtx);
  const [clients, setClients] = useState<{ name: string; id: React.ReactText }[]>([]);
  const [loading, setLoading] = useState(false);

  const cProps = useMemo(() => getCommonProps(ctx.isDetails), [ctx.isDetails]);

  const handleSubmit = useCallback(
    async (data: TPA) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name } = data;
      setLoading(true);
      if (submit) {
        const resp = await submit(data, '2 - Avaliação de Maturidade');
        if (resp) {
          functionCallback(index + 1);
        }
      } else {
        functionCallback(index + 1);
      }
      setLoading(false);
    },
    [submit, functionCallback, ctx.handlePayload],
  );

  const handleField = useCallback(
    (
      k1: keyof TPA['jsonMaturityQuestions'],
      k2: keyof CommentedAnswer,
      e: React.ChangeEvent<HTMLInputElement> | yo,
    ) => {
      ctx.handlePayload(dt => {
        const data = {
          ...dt,
        };
        if (k2 === 'comments' && typeof e !== 'string')
          data.jsonMaturityQuestions[k1][k2] = e.target.value;
        else if (typeof e === 'string') data.jsonMaturityQuestions[k1][k2] = e;
        return data;
      });
    },
    [ctx.handlePayload],
  );

  useEffect(() => {
    if (checkPermission({ onlyAdmin: true })) {
      (async () => {
        const { data } = await getAllClients('');
        setClients(data);
      })();
    }
  }, []);

  useEffect(() => {
    if (ctx.isDetails && clients.length > 0) {
      if (ctx.setClient) ctx.setClient(clients.find(i => i.id === ctx.data.clientId));
    }
  }, [ctx.isDetails, clients]);

  const clientSelect = clients.find(i => +i.id === +(getLoggedUser().client || '')) as ClientProps;

  return (
    <Flex marginTop="1rem" width="100%" direction="column">
      <Unform initialData={ctx.data} onSubmit={handleSubmit}>
        {checkPermission({ onlyAdmin: true }) ? (
          <>
            {!ctx.isDetails ? (
              <AuthorizeView onlyAdmin>
                <Box mb="20px">
                  {clients.length > 0 ? (
                    !getLoggedUser()?.client ? (
                      <AutocompleteInput
                        name="clientId"
                        optionLabel="name"
                        optionValue="id"
                        label="Cliente"
                        options={
                          clients
                            ?.map(client => ({ name: client.name, id: client.id }))
                            .sort((a, b) => {
                              return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                            }) || []
                        }
                        placeholder={ctx.client?.name}
                        defaultValue={ctx.client}
                        {...cProps}
                      />
                    ) : (
                      <SelectInput
                        name="clientId"
                        optionLabel="name"
                        optionValue="id"
                        label="Cliente"
                        options={
                          clients
                            ?.map(client => ({ name: client.name, id: client.id }))
                            .sort((a, b) => {
                              return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                            }) || []
                        }
                        defaultValues={[clientSelect]}
                        {...cProps}
                        disabled
                      />
                    )
                  ) : (
                    <Loading />
                  )}
                </Box>
              </AuthorizeView>
            ) : (
              <AuthorizeView onlyAdmin>
                <Box mb="20px">
                  {clients.length > 0 ? (
                    <AutocompleteInput
                      name="clientId"
                      optionLabel="name"
                      optionValue="id"
                      label="Cliente"
                      options={clients}
                      placeholder={ctx.client?.name}
                      defaultValue={ctx.client}
                      {...cProps}
                    />
                  ) : (
                    <Loading />
                  )}
                </Box>
              </AuthorizeView>
            )}

            <Box mb="6px">
              <TextInput
                label="Identificação de terceiro"
                name="name"
                // {...cProps}
              />
            </Box>
            <Box mb="24px">
              <Label
                tip={`Pela atual redação da LGPD, empresas na condição de "operadoras" de tratamento de dados pessoais não são obrigadas a nomear um DPO, mas sua ausência na companhia demonstra a ausência de uma política interna estruturada para proteger os dados pessoais que sua empresa armazena.`}
              >
                O fornecedor possui um encarregado pelo tratamento de dados pessoais (DPO)? Se sim,
                informe seu nome e contato.
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasDPO"
                  // {...cProps}
                  label={ctx.data.jsonMaturityQuestions?.hasDPO.answer === 'yes' ? 'Sim' : 'Não'}
                  value={ctx.data.jsonMaturityQuestions?.hasDPO.answer}
                  onChange={handleField.bind(null, 'hasDPO', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasDPO.comments}
                  onChangeEvent={handleField.bind(null, 'hasDPO', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Com a LGPD em vigor desde 18.09.2020, as empresas devem manter tal inventário/registro. Além disso, sua ausência sinaliza possível ausência de suficiente governança em privacidade e proteção de dados."
              >
                O fornecedor possui inventário/registro das suas atividades que envolvem o uso de
                dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasInventary"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasInventary.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasInventary.answer}
                  onChange={handleField.bind(null, 'hasInventary', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasInventary.comments}
                  onChangeEvent={handleField.bind(null, 'hasInventary', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Essa medida evidencia a robustez do programa de compliance e governança em privacidade/proteção de dados, já que aumenta a probabilidade de descoberta e mitigação de riscos."
              >
                O fornecedor realiza periodicamente avaliações de impacto à privacidade/proteção de
                dados quanto aos seus processos?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="impactAvaliation"
                  // {...cProps}
                  label={
                    ctx.data.jsonMaturityQuestions?.impactAvaliation.answer === 'yes'
                      ? 'Sim'
                      : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.impactAvaliation.answer}
                  onChange={handleField.bind(null, 'impactAvaliation', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.impactAvaliation.comments}
                  onChangeEvent={handleField.bind(null, 'impactAvaliation', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A LGPD determina que os incidentes de segurança sejam comunicados à Autoridade Nacional de Proteção de Dados e aos titulares de dados. Se o fornecedor não possui um Plano de resposta a esse tipo de incidente, dificilmente as partes do contrato conseguirão cumprir o dever de comunicação. Além disso, referido plano é expressamente mencionado no artigo 50, §2o, I, g, da LGPD."
              >
                O fornecedor possui um Plano de respostas a incidentes relativos a dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasPlans"
                  label={ctx.data.jsonMaturityQuestions?.hasPlans.answer === 'yes' ? 'Sim' : 'Não'}
                  value={ctx.data.jsonMaturityQuestions?.hasPlans.answer}
                  onChange={handleField.bind(null, 'hasPlans', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasPlans.comments}
                  onChangeEvent={handleField.bind(null, 'hasPlans', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Ausência de Política de Segurança de Informação representa falta de zelo do fornecedor em relação ao tema."
              >
                O fornecedor possui uma Política de Segurança da Informação?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasPolicy"
                  label={ctx.data.jsonMaturityQuestions?.hasPolicy.answer === 'yes' ? 'Sim' : 'Não'}
                  value={ctx.data.jsonMaturityQuestions?.hasPolicy.answer}
                  onChange={handleField.bind(null, 'hasPolicy', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasPolicy.comments}
                  onChangeEvent={handleField.bind(null, 'hasPolicy', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Essa medida serve para assegurar que apenas as pessoas que realmente precisem poderão acessar/visualizar dados pessoais. Sua existência pode servir como indício de atendimento ao princípio da necessidade, previsto na LGPD."
              >
                O fornecedor realiza controle de acesso aos dados pessoais que armazena?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasControlData"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasControlData.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasControlData.answer}
                  onChange={handleField.bind(null, 'hasControlData', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasControlData.comments}
                  onChangeEvent={handleField.bind(null, 'hasControlData', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="O envolvimento de mais uma empresa que terá acesso aos dados demanda cuidados adicionais relacionados ao controle de acesso e segurança da informação."
              >
                Algum funcionário de outra empresa terá acesso aos dados pessoais enviados em
                decorrência deste contrato (subcontratação/quarteirização)?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasThirdPartyAccess"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasThirdPartyAccess.answer === 'yes'
                      ? 'Sim'
                      : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasThirdPartyAccess.answer}
                  onChange={handleField.bind(null, 'hasThirdPartyAccess', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasThirdPartyAccess.comments}
                  onChangeEvent={handleField.bind(null, 'hasThirdPartyAccess', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A ausência da política de compliance pode indicar que a proteção de dados pessoais ainda não é um tema relevante para o fornecedor."
              >
                O fornecedor possui uma política de compliance em proteção de dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasCompliance"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasCompliance.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasCompliance.answer}
                  onChange={handleField.bind(null, 'hasCompliance', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasCompliance.comments}
                  onChangeEvent={handleField.bind(null, 'hasCompliance', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A existência de uma área/comitê responsável pelo programa de governança indica que a proteção de dados pessoais é um tema relevante para o fornecedor."
              >
                O fornecedor possui uma área/comitê responsável pelo programa de governança em
                privacidade/proteção de dados?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasGovernance"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasGovernance.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasGovernance.answer}
                  onChange={handleField.bind(null, 'hasGovernance', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasGovernance.comments}
                  onChangeEvent={handleField.bind(null, 'hasGovernance', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A inexistência de um canal de comunicação pode representar ausência de medidas técnicas e organizacionais suficientes para o cumprimento da LGPD."
              >
                O fornecedor possui um canal de comunicação para solicitações e exercício dos
                direitos dos titulares?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasComunicationChannel"
                  // {...cProps}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasComunicationChannel.answer === 'yes'
                      ? 'Sim'
                      : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasComunicationChannel.answer}
                  onChange={handleField.bind(null, 'hasComunicationChannel', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasComunicationChannel.comments}
                  onChangeEvent={handleField.bind(null, 'hasComunicationChannel', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Processos judiciais dessa natureza podem elevar a criticidade da contratação, em razão das possíveis repercussões negativas (tanto financeiras, quanto reputacionais)."
              >
                O fornecedor responde por algum processo judicial que tenha por objeto violação às
                leis de proteção de dados?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasLegalProcess"
                  // {...cProps}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasLegalProcess.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasLegalProcess.answer}
                  onChange={handleField.bind(null, 'hasLegalProcess', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasLegalProcess.comments}
                  onChangeEvent={handleField.bind(null, 'hasLegalProcess', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Incidentes de Segurança da Informação podem denotar riscos no ambiente do fornecedor."
              >
                O fornecedor já esteve envolvido em um incidente de segurança relacionado a dados
                pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasIncident"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasIncident.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasIncident.answer}
                  onChange={handleField.bind(null, 'hasIncident', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasIncident.comments}
                  onChangeEvent={handleField.bind(null, 'hasIncident', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Procedimentos movidos por tais entidades podem indicar violação às leis de proteção de dados pessoais, além de abalo reputacional à contratação."
              >
                O fornecedor é atualmente alvo de alguma investigação por parte de Ministério
                Públicos, Procons, IDEC ou entidades semelhantes (governamentais ou não) em temas
                relacionados a dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasInvestigated"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasInvestigated.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasInvestigated.answer}
                  onChange={handleField.bind(null, 'hasInvestigated', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasInvestigated.comments}
                  onChangeEvent={handleField.bind(null, 'hasInvestigated', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A realização de treinamentos periódicos em segurança da informação e compliance em proteção de dados indica que o fornecedor emprega esforçcos em garantir a efetividade de seu programa de compliance e governança em privacidade/proteção de dados."
              >
                Os funcionários do fornecedor recebem treinamentos relacionados a segurança da
                informação e compliance em privacidade/proteção de dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  // {...cProps}
                  name="hasTrainning"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasTrainning.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasTrainning.answer}
                  onChange={handleField.bind(null, 'hasTrainning', 'answer')}
                />
                <AddObservation
                  // isDetail={ctx.isDetails}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasTrainning.comments}
                  onChangeEvent={handleField.bind(null, 'hasTrainning', 'comments')}
                />
              </Box>
            </Box>

            {!ctx.isDetails && (
              <Box width="100%">
                <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
                  <Button
                    type="button"
                    variant="primary"
                    outline
                    onClick={() => functionCallback(index - 1)}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    // onClick={nextPage}
                    isLoading={loading}
                    disabled={loading}
                  >
                    Próximo
                  </Button>
                </Stack>
              </Box>
            )}
            {ctx.isDetails && (
              <Box width="100%">
                <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
                  <Button
                    type="button"
                    variant="primary"
                    outline
                    onClick={() => functionCallback(index - 1)}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    // onClick={nextPage}
                    isLoading={loading}
                    disabled={loading}
                  >
                    Próximo
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box mb="6px">
              <TextInput label="Identificação de terceiro" name="name" isDisabled={completed} />
            </Box>
            <Box mb="24px">
              <Label
                tip={`Pela atual redação da LGPD, empresas na condição de "operadoras" de tratamento de dados pessoais não são obrigadas a nomear um DPO, mas sua ausência na companhia demonstra a ausência de uma política interna estruturada para proteger os dados pessoais que sua empresa armazena.`}
              >
                O fornecedor possui um encarregado pelo tratamento de dados pessoais (DPO)? Se sim,
                informe seu nome e contato.
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasDPO"
                  isDisabled={completed}
                  label={ctx.data.jsonMaturityQuestions?.hasDPO.answer === 'yes' ? 'Sim' : 'Não'}
                  value={ctx.data.jsonMaturityQuestions?.hasDPO.answer}
                  onChange={handleField.bind(null, 'hasDPO', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasDPO.comments}
                  onChangeEvent={handleField.bind(null, 'hasDPO', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Com a LGPD em vigor desde 18.09.2020, as empresas devem manter tal inventário/registro. Além disso, sua ausência sinaliza possível ausência de suficiente governança em privacidade e proteção de dados."
              >
                O fornecedor possui inventário/registro das suas atividades que envolvem o uso de
                dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasInventary"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasInventary.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasInventary.answer}
                  onChange={handleField.bind(null, 'hasInventary', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasInventary.comments}
                  onChangeEvent={handleField.bind(null, 'hasInventary', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Essa medida evidencia a robustez do programa de compliance e governança em privacidade/proteção de dados, já que aumenta a probabilidade de descoberta e mitigação de riscos."
              >
                O fornecedor realiza periodicamente avaliações de impacto à privacidade/proteção de
                dados quanto aos seus processos?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="impactAvaliation"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.impactAvaliation.answer === 'yes'
                      ? 'Sim'
                      : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.impactAvaliation.answer}
                  onChange={handleField.bind(null, 'impactAvaliation', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.impactAvaliation.comments}
                  onChangeEvent={handleField.bind(null, 'impactAvaliation', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A LGPD determina que os incidentes de segurança sejam comunicados à Autoridade Nacional de Proteção de Dados e aos titulares de dados. Se o fornecedor não possui um Plano de resposta a esse tipo de incidente, dificilmente as partes do contrato conseguirão cumprir o dever de comunicação. Além disso, referido plano é expressamente mencionado no artigo 50, §2o, I, g, da LGPD."
              >
                O fornecedor possui um Plano de respostas a incidentes relativos a dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasPlans"
                  isDisabled={completed}
                  label={ctx.data.jsonMaturityQuestions?.hasPlans.answer === 'yes' ? 'Sim' : 'Não'}
                  value={ctx.data.jsonMaturityQuestions?.hasPlans.answer}
                  onChange={handleField.bind(null, 'hasPlans', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasPlans.comments}
                  onChangeEvent={handleField.bind(null, 'hasPlans', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Ausência de Política de Segurança de Informação representa falta de zelo do fornecedor em relação ao tema."
              >
                O fornecedor possui uma Política de Segurança da Informação?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasPolicy"
                  isDisabled={completed}
                  label={ctx.data.jsonMaturityQuestions?.hasPolicy.answer === 'yes' ? 'Sim' : 'Não'}
                  value={ctx.data.jsonMaturityQuestions?.hasPolicy.answer}
                  onChange={handleField.bind(null, 'hasPolicy', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasPolicy.comments}
                  onChangeEvent={handleField.bind(null, 'hasPolicy', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Essa medida serve para assegurar que apenas as pessoas que realmente precisem poderão acessar/visualizar dados pessoais. Sua existência pode servir como indício de atendimento ao princípio da necessidade, previsto na LGPD."
              >
                O fornecedor realiza controle de acesso aos dados pessoais que armazena?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasControlData"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasControlData.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasControlData.answer}
                  onChange={handleField.bind(null, 'hasControlData', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasControlData.comments}
                  onChangeEvent={handleField.bind(null, 'hasControlData', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="O envolvimento de mais uma empresa que terá acesso aos dados demanda cuidados adicionais relacionados ao controle de acesso e segurança da informação."
              >
                Algum funcionário de outra empresa terá acesso aos dados pessoais enviados em
                decorrência deste contrato (subcontratação/quarteirização)?
              </Label>
              <Box mt="8px">
                <RadioInput
                  isDisabled={completed}
                  name="hasThirdPartyAccess"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasThirdPartyAccess.answer === 'yes'
                      ? 'Sim'
                      : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasThirdPartyAccess.answer}
                  onChange={handleField.bind(null, 'hasThirdPartyAccess', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasThirdPartyAccess.comments}
                  onChangeEvent={handleField.bind(null, 'hasThirdPartyAccess', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A ausência da política de compliance pode indicar que a proteção de dados pessoais ainda não é um tema relevante para o fornecedor."
              >
                O fornecedor possui uma política de compliance em proteção de dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasCompliance"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasCompliance.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasCompliance.answer}
                  onChange={handleField.bind(null, 'hasCompliance', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasCompliance.comments}
                  onChangeEvent={handleField.bind(null, 'hasCompliance', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A existência de uma área/comitê responsável pelo programa de governança indica que a proteção de dados pessoais é um tema relevante para o fornecedor."
              >
                O fornecedor possui uma área/comitê responsável pelo programa de governança em
                privacidade/proteção de dados?
              </Label>
              <Box mt="8px">
                <RadioInput
                  isDisabled={completed}
                  name="hasGovernance"
                  label={
                    ctx.data.jsonMaturityQuestions?.hasGovernance.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasGovernance.answer}
                  onChange={handleField.bind(null, 'hasGovernance', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasGovernance.comments}
                  onChangeEvent={handleField.bind(null, 'hasGovernance', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A inexistência de um canal de comunicação pode representar ausência de medidas técnicas e organizacionais suficientes para o cumprimento da LGPD."
              >
                O fornecedor possui um canal de comunicação para solicitações e exercício dos
                direitos dos titulares?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasComunicationChannel"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasComunicationChannel.answer === 'yes'
                      ? 'Sim'
                      : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasComunicationChannel.answer}
                  onChange={handleField.bind(null, 'hasComunicationChannel', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasComunicationChannel.comments}
                  onChangeEvent={handleField.bind(null, 'hasComunicationChannel', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Processos judiciais dessa natureza podem elevar a criticidade da contratação, em razão das possíveis repercussões negativas (tanto financeiras, quanto reputacionais)."
              >
                O fornecedor responde por algum processo judicial que tenha por objeto violação às
                leis de proteção de dados?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasLegalProcess"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasLegalProcess.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasLegalProcess.answer}
                  onChange={handleField.bind(null, 'hasLegalProcess', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasLegalProcess.comments}
                  onChangeEvent={handleField.bind(null, 'hasLegalProcess', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Incidentes de Segurança da Informação podem denotar riscos no ambiente do fornecedor."
              >
                O fornecedor já esteve envolvido em um incidente de segurança relacionado a dados
                pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasIncident"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasIncident.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasIncident.answer}
                  onChange={handleField.bind(null, 'hasIncident', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasIncident.comments}
                  onChangeEvent={handleField.bind(null, 'hasIncident', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="Procedimentos movidos por tais entidades podem indicar violação às leis de proteção de dados pessoais, além de abalo reputacional à contratação."
              >
                O fornecedor é atualmente alvo de alguma investigação por parte de Ministério
                Públicos, Procons, IDEC ou entidades semelhantes (governamentais ou não) em temas
                relacionados a dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasInvestigated"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasInvestigated.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasInvestigated.answer}
                  onChange={handleField.bind(null, 'hasInvestigated', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasInvestigated.comments}
                  onChangeEvent={handleField.bind(null, 'hasInvestigated', 'comments')}
                />
              </Box>
            </Box>
            <Box mb="24px">
              <Label
                flex={1}
                tip="A realização de treinamentos periódicos em segurança da informação e compliance em proteção de dados indica que o fornecedor emprega esforçcos em garantir a efetividade de seu programa de compliance e governança em privacidade/proteção de dados."
              >
                Os funcionários do fornecedor recebem treinamentos relacionados a segurança da
                informação e compliance em privacidade/proteção de dados pessoais?
              </Label>
              <Box mt="8px">
                <RadioInput
                  name="hasTrainning"
                  isDisabled={completed}
                  label={
                    ctx.data.jsonMaturityQuestions?.hasTrainning.answer === 'yes' ? 'Sim' : 'Não'
                  }
                  value={ctx.data.jsonMaturityQuestions?.hasTrainning.answer}
                  onChange={handleField.bind(null, 'hasTrainning', 'answer')}
                />
                <AddObservation
                  isDetail={completed}
                  placeholder="Observações"
                  obsValue={ctx.data.jsonMaturityQuestions.hasTrainning.comments}
                  onChangeEvent={handleField.bind(null, 'hasTrainning', 'comments')}
                />
              </Box>
            </Box>

            {!completed && (
              <Box width="100%">
                <Stack direction={['column', 'row']} spacing="1rem" mt="1rem" justify="flex-end">
                  <Button
                    type="button"
                    variant="primary"
                    outline
                    onClick={() => functionCallback(index - 1)}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    // onClick={nextPage}
                    isLoading={loading}
                    disabled={loading}
                  >
                    Próximo
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Unform>
    </Flex>
  );
};

export const MaturityEvaluation = memo(Item);
